import { List, Map } from 'immutable';

import { ATTRIBUTE_DEFAULTS, DEFAULT_OPTION, DEFAULT_VALUES, DEFAULT_VARIANT, ENABLED, ERROR, NEW, OVERSET, REQUIRED, VALUE } from '../../constants/product';
import { ALL, COMBINATIONS, INFLUENCE, VARIANTS } from '../../constants/variations';
import { SHOPIFY_CATEGORY_ENABLED } from '../../constants/featureFlags';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';
import ATTRIBUTE from '../../constants/attributes';

function makeValue(value) {
  return Map({ [VALUE]: value });
}

export function newDetails({ channel }) {
  switch (channel) {
    case ETSY: {
      return Map({
        [ATTRIBUTE.CATEGORY]: DEFAULT_VALUES.ZERO,
        [ATTRIBUTE.DIGITAL]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.IS_SUPPLY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.MATERIALS]: DEFAULT_VALUES.EMPTY_ORDERED_MAP,
        [ATTRIBUTE.PRODUCTION_PARTNERS]: DEFAULT_VALUES.EMPTY_LIST,
        [ATTRIBUTE.SECTION]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.SHOULD_AUTO_RENEW]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.TAXONOMY_ATTRIBUTES]: EMPTY.MAP,
        [ATTRIBUTE.WHEN_MADE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WHO_MADE]: DEFAULT_VALUES.NULL,
      });
    }

    case SHOPIFY: {
      const details = Map({
        [ATTRIBUTE.COLLECTIONS]: DEFAULT_VALUES.EMPTY_LIST,
        [ATTRIBUTE.PRODUCT_TYPE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.VENDOR]: DEFAULT_VALUES.NULL,
      });

      return SHOPIFY_CATEGORY_ENABLED
        ? details.set(ATTRIBUTE.CATEGORY, DEFAULT_VALUES.NULL)
        : details;
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function newInventory({ channel, emptyValues }) {
  const inventory = Map({
    [ATTRIBUTE.PRICE]: emptyValues ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO_CENT,
    [ATTRIBUTE.SKU]: DEFAULT_VALUES.EMPTY_STRING,
  });

  switch (channel) {
    case ETSY: {
      return inventory.set(ATTRIBUTE.QUANTITY,
        emptyValues
          ? DEFAULT_VALUES.EMPTY_STRING
          : DEFAULT_VALUES.ONE
      );
    }

    case SHOPIFY: {
      const DEFAULT_VALUE = Map({
        options: List([DEFAULT_OPTION]),
        variants: List([DEFAULT_VARIANT]),
      });

      return inventory
        .set(ATTRIBUTE.BARCODE, DEFAULT_VALUES.EMPTY_STRING)
        .set(ATTRIBUTE.CAP, DEFAULT_VALUES.EMPTY_STRING)
        .set(ATTRIBUTE.CHARGE_TAX, DEFAULT_VALUES.TRUE)
        .set(ATTRIBUTE.CONTINUE_SELLING, DEFAULT_VALUES.FALSE)
        .set(ATTRIBUTE.COUNTRY_CODE, DEFAULT_VALUES.NULL)
        .set(ATTRIBUTE.CPI, DEFAULT_VALUES.EMPTY_STRING)
        .set(ATTRIBUTE.DEFAULT_VALUE, DEFAULT_VALUE)
        .set(ATTRIBUTE.HS_CODE, DEFAULT_VALUES.NULL)
        .set(ATTRIBUTE.PHYSICAL, DEFAULT_VALUES.TRUE)
        .set(ATTRIBUTE.QUANTITY, emptyValues ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO)
        .set(ATTRIBUTE.TRACK_QUANTITY, DEFAULT_VALUES.TRUE)
        .set(ATTRIBUTE.UNIT, DEFAULT_VALUES.UNIT)
        .set(ATTRIBUTE.WEIGHT, emptyValues ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO);
    }

    default: {
      return inventory;
    }
  }
}

function newPersonalization() {
  return Map({
    [ENABLED]: false,
    [ATTRIBUTE.CHAR_COUNT_MAX]: DEFAULT_VALUES.NULL,
    [ATTRIBUTE.INSTRUCTIONS]: DEFAULT_VALUES.EMPTY_STRING,
    [REQUIRED]: false,
  });
}

export function newShipping({ channel }) {
  switch (channel) {
    case ETSY: {
      return Map({
        [ATTRIBUTE.HEIGHT]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.LENGTH]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.PROFILE_ID]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.PROFILE_TYPE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.RETURN_POLICY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WEIGHT]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WIDTH]: DEFAULT_VALUES.NULL,
      });
    }

    default: {
      return undefined;
    }
  }
}

export function newVariations({ channel }) {
  const variations = Map({
    [COMBINATIONS]: EMPTY.LIST,
    [VARIANTS]: EMPTY.LIST,
    [ERROR]: EMPTY.MAP,
  });

  switch (channel) {
    case ETSY: {
      const influence = Map({
        [ALL]: false,
        [ATTRIBUTE.PRICE]: EMPTY.MAP,
        [ATTRIBUTE.QUANTITY]: EMPTY.MAP,
        [ATTRIBUTE.SKU]: EMPTY.MAP,
      });

      return variations.set(INFLUENCE, influence);
    }

    case SHOPIFY: {
      const influence = Map({ [ATTRIBUTE.TRACK_QUANTITY]: false });
      return variations.set(INFLUENCE, influence);
    }

    default: {
      return variations;
    }
  }
}

export function newProduct({ channel, emptyValues }) {
  let product = Map({
    channel,
    productId: NEW,
    [FORM.DESCRIPTION]: makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.DESCRIPTION]),
    [FORM.DETAILS]: newDetails({ channel }),
    [FORM.INVENTORY]: newInventory({ channel, emptyValues }),
    [FORM.PHOTOS]: makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.PHOTOS]),
    [FORM.VIDEOS]: makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.VIDEOS]),
    [FORM.TAGS]: makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TAGS]),
    [FORM.TITLE]: makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TITLE]),
    [FORM.VARIATIONS]: newVariations({ channel }),
  });

  switch (channel) {
    case ETSY: {
      const personalization = newPersonalization();
      const shipping = newShipping({ channel });

      product = product
        .set(FORM.PERSONALIZATION, personalization)
        .set(FORM.SHIPPING, shipping);

      break;
    }

    case SHOPIFY: {
      const seo = Map({
        [ATTRIBUTE.META_DESCRIPTION]: DEFAULT_VALUES.EMPTY_STRING.set(OVERSET, true),
        [ATTRIBUTE.PAGE_TITLE]: DEFAULT_VALUES.EMPTY_STRING.set(OVERSET, true),
      });

      product = product.set(FORM.SEO, seo);

      break;
    }

    default: {
      break;
    }
  }

  return product;
}
